import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { esModoIdentidadLocal, resolveSessionState, type EstadoSesion } from '@/app/core/access/route-profile-access';
import { NAV_SECTIONS } from '@/app/shared/nav/nav-sections.token';
import { NavItem, NavSection } from '@/app/shared/nav/nav-section.model';
import { IDENTIDAD_STORAGE_KEY } from '@/app/features/catalogos-comerciales/models/identidades-sinteticas';
import { IDENTITY_CHANGED_EVENT } from '@/app/features/catalogos-comerciales/services/acceso-contexto.service';

type PerfilVisible = 'administrador' | 'agente_ventas' | 'jefe_comercial' | 'director' | 'soporte';

function resolvePerfil(identityId: string | null): PerfilVisible {
  switch (identityId) {
    case 'corp-admin-01':
      return 'administrador';
    case 'corp-jefe-01':
      return 'jefe_comercial';
    case 'corp-director-01':
      return 'director';
    case 'corp-soporte-01':
      return 'soporte';
    default:
      return 'agente_ventas';
  }
}

@Injectable({ providedIn: 'root' })
export class ShellStore {
  private readonly config  = inject(APP_CONFIG);
  private readonly router  = inject(Router);
  private readonly doc     = inject(DOCUMENT);

  private readonly rawSections =
    (inject(NAV_SECTIONS, { optional: true }) as NavSection[] | null) ?? [];

  readonly sidebarCollapsed = signal(false);
  readonly drilledItem      = signal<NavItem | null>(null);
  readonly animDir          = signal<'in' | 'out' | 'none'>('none');
  readonly mobileViewport   = signal(false);

  readonly activeUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.doc.location.pathname },
  );

  readonly perfilVisible = signal<PerfilVisible>(this.readPerfilVisible());
  readonly estadoSesion = signal<EstadoSesion>(this.readEstadoSesion());

  readonly sections = computed<NavSection[]>(() => {
    if (this.estadoSesion() !== 'activa') {
      return [];
    }

    return this.rawSections
      .filter((section) => this.matchesProfile(section.profiles))
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => this.matchesProfile(item.profiles)),
      }))
      .filter((section) => section.items.length > 0);
  });
  readonly envLabel = this.config.environment;
  readonly panelDesarrolloLocalVisible = computed(() => this.config.panelDesarrolloLocalVisible);
  private readonly viewportQuery = this.doc.defaultView?.matchMedia('(max-width: 1024px)') ?? null;

  constructor() {
    this.mobileViewport.set(this.isNarrowViewport());
    this.sidebarCollapsed.set(this.mobileViewport());
    effect(() => this._syncFromUrl(this.activeUrl() ?? ''));
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener(IDENTITY_CHANGED_EVENT, this.handleIdentityChanged);
    this.viewportQuery?.addEventListener('change', this.handleViewportChange);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => !v);
  }

  closeSidebar(): void {
    this.sidebarCollapsed.set(true);
  }

  isMobileViewport(): boolean {
    return this.mobileViewport();
  }

  drillIn(item: NavItem): void {
    if (!item.children?.length) {
      if (item.route) this.router.navigate([item.route]);
      return;
    }
    this.animDir.set('in');
    this.drilledItem.set(item);
    const leaf = this._firstLeaf(item.children);
    if (leaf?.route) this.router.navigate([leaf.route]);
  }

  drillOut(): void {
    this.animDir.set('out');
    this.drilledItem.set(null);
  }

  isItemActive(item: NavItem): boolean {
    const url = this.activeUrl() ?? '';
    if (item.route) {
      return item.route === '/'
        ? url === '/'
        : url === item.route || url.startsWith(item.route + '/');
    }
    return item.children ? this._urlMatchesAnyDescendant(item.children, url) : false;
  }

  private _syncFromUrl(url: string): void {
    for (const section of this.rawSections) {
      for (const item of section.items) {
        if (item.children?.length && this._urlMatchesAnyDescendant(item.children, url)) {
          this.drilledItem.set(item);
          return;
        }
      }
    }
    this.drilledItem.set(null);
  }

  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (event.key === IDENTIDAD_STORAGE_KEY) {
      this.perfilVisible.set(this.readPerfilVisible());
      this.estadoSesion.set(this.readEstadoSesion());
    }
  };

  private readonly handleIdentityChanged = (): void => {
    this.perfilVisible.set(this.readPerfilVisible());
    this.estadoSesion.set(this.readEstadoSesion());
  };

  private readonly handleViewportChange = (event: MediaQueryListEvent): void => {
    this.mobileViewport.set(event.matches);

    if (event.matches) {
      this.closeSidebar();
    }
  };

  private readPerfilVisible(): PerfilVisible {
    try {
      const raw = this.doc.defaultView?.sessionStorage.getItem(IDENTIDAD_STORAGE_KEY);
      if (!raw) {
        return 'agente_ventas';
      }

      const parsed = JSON.parse(raw) as { corporateIdentityId?: string };
      return resolvePerfil(parsed.corporateIdentityId ?? null);
    } catch {
      return 'agente_ventas';
    }
  }

  private readEstadoSesion(): EstadoSesion {
    return resolveSessionState(this.doc.defaultView?.sessionStorage, esModoIdentidadLocal(this.config));
  }

  private matchesProfile(profiles: string[] | undefined): boolean {
    return !profiles?.length || profiles.includes(this.perfilVisible());
  }

  private _urlMatchesAnyDescendant(items: NavItem[], url: string): boolean {
    return items.some(item => {
      if (item.route) {
        return item.route === '/'
          ? url === '/'
          : url === item.route || url.startsWith(item.route + '/');
      }
      return item.children ? this._urlMatchesAnyDescendant(item.children, url) : false;
    });
  }

  private _firstLeaf(items: NavItem[]): NavItem | null {
    for (const item of items) {
      if (item.route) return item;
      if (item.children) {
        const found = this._firstLeaf(item.children);
        if (found) return found;
      }
    }
    return null;
  }

  private isNarrowViewport(): boolean {
    return this.doc.defaultView?.matchMedia('(max-width: 1024px)').matches ?? false;
  }
}
